import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Park } from '../types';
import type {
  DockingFilter,
  DockingRecord,
  DockingStatistics,
  ParkDockingSummary,
} from '../types/docking';
import { DEFAULT_DOCKING_FILTER } from '../types/docking';
import {
  fetchDockingRecords,
  saveDockingRecord,
  deleteDockingRecord as apiDelete,
} from '../api/docking';

export interface UseDockingResult {
  records: DockingRecord[];
  loading: boolean;
  error: string | null;
  filter: DockingFilter;
  setFilter: React.Dispatch<React.SetStateAction<DockingFilter>>;
  statistics: DockingStatistics;
  dockedParks: Park[];
  parkSummaries: ParkDockingSummary[];
  selectedParkId: string | null;
  setSelectedParkId: (id: string | null) => void;
  selectedParkRecords: DockingRecord[];
  saveRecord: (record: DockingRecord) => void;
  deleteRecord: (id: string) => void;
  reload: () => void;
}

export function useDocking(allParks: Park[]): UseDockingResult {
  const [allRecords, setAllRecords] = useState<DockingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DockingFilter>(DEFAULT_DOCKING_FILTER);
  const [selectedParkId, setSelectedParkId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDockingRecords();
      setAllRecords(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '对接记录加载失败');
      setAllRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const parkMap = useMemo(() => {
    const m = new Map<string, Park>();
    allParks.forEach((p) => m.set(p.id, p));
    return m;
  }, [allParks]);

  const filteredRecords = useMemo(() => {
    return allRecords.filter((r) => {
      const park = parkMap.get(r.parkId);
      if (!park) return false;

      if (filter.parkLevel !== 'all' && park.parkLevel !== filter.parkLevel) {
        return false;
      }
      if (filter.maturity != null) {
        if (r.maturity !== filter.maturity) return false;
      }
      if (filter.dateRange) {
        const [from, to] = filter.dateRange;
        if (r.dockedAt < from || r.dockedAt > to) return false;
      }
      return true;
    });
  }, [allRecords, filter, parkMap]);

  const statistics = useMemo<DockingStatistics>(() => {
    const dockedParkIds = new Set(filteredRecords.map((r) => r.parkId));
    let dockedNational = 0;
    let dockedProvincial = 0;
    let dockedPending = 0;
    let totalNational = 0;
    let totalProvincial = 0;
    let totalPending = 0;

    allParks.forEach((p) => {
      if (p.parkLevel === 'national') {
        totalNational++;
        if (dockedParkIds.has(p.id)) dockedNational++;
      } else if (p.parkLevel === 'provincial') {
        totalProvincial++;
        if (dockedParkIds.has(p.id)) dockedProvincial++;
      } else {
        totalPending++;
        if (dockedParkIds.has(p.id)) dockedPending++;
      }
    });
    return {
      dockedNational,
      dockedProvincial,
      dockedPending,
      totalNational,
      totalProvincial,
      totalPending,
    };
  }, [filteredRecords, allParks]);

  const dockedParks = useMemo<Park[]>(() => {
    // 地图散点与侧栏筛选保持一致：成熟度/层级/时间过滤后只展示可见园区
    const ids = new Set(filteredRecords.map((r) => r.parkId));
    return allParks.filter((p) => ids.has(p.id));
  }, [filteredRecords, allParks]);

  const parkSummaries = useMemo<ParkDockingSummary[]>(() => {
    const grouped = new Map<string, DockingRecord[]>();
    filteredRecords.forEach((r) => {
      const list = grouped.get(r.parkId) ?? [];
      list.push(r);
      grouped.set(r.parkId, list);
    });

    const summaries: ParkDockingSummary[] = [];
    grouped.forEach((records, parkId) => {
      const park = parkMap.get(parkId);
      if (!park) return;
      const sorted = [...records].sort((a, b) => b.dockedAt.localeCompare(a.dockedAt));
      summaries.push({
        parkId,
        parkName: park.name,
        parkLevel: park.parkLevel,
        province: park.province,
        city: park.city,
        recordCount: records.length,
        latestDockedAt: sorted[0].dockedAt,
        latestCounterpartyOrg: sorted[0].counterpartyOrg,
      });
    });

    summaries.sort((a, b) => b.latestDockedAt.localeCompare(a.latestDockedAt));
    return summaries;
  }, [filteredRecords, parkMap]);

  useEffect(() => {
    if (!selectedParkId) return;
    const stillVisible = parkSummaries.some((s) => s.parkId === selectedParkId);
    if (!stillVisible) {
      setSelectedParkId(null);
    }
  }, [parkSummaries, selectedParkId]);

  const selectedParkRecords = useMemo(() => {
    if (!selectedParkId) return [];
    return filteredRecords
      .filter((r) => r.parkId === selectedParkId)
      .sort((a, b) => b.dockedAt.localeCompare(a.dockedAt));
  }, [filteredRecords, selectedParkId]);

  const saveRecord = useCallback((record: DockingRecord) => {
    saveDockingRecord(record);
    setAllRecords((prev) => {
      const idx = prev.findIndex((r) => r.id === record.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = record;
        return next;
      }
      return [...prev, record];
    });
  }, []);

  const deleteRecord = useCallback((id: string) => {
    apiDelete(id);
    setAllRecords((prev) => prev.filter((r) => r.id !== id));
  }, []);

  return {
    records: filteredRecords,
    loading,
    error,
    filter,
    setFilter,
    statistics,
    dockedParks,
    parkSummaries,
    selectedParkId,
    setSelectedParkId,
    selectedParkRecords,
    saveRecord,
    deleteRecord,
    reload: load,
  };
}
